import { create } from '@bufbuild/protobuf'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import {
    GrpcClientsContextValue,
    useGrpcClients,
} from '@/contexts/GrpcClientContext'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import {
    ProjectFiltersDto,
    ProjectFiltersDtoSchema,
} from '@/generated/Messages/Filters_pb'
import { assertNotNullish } from '@/helper/assertNotNullish'
import { loadAllIter } from '@/helper/loadAllIter'
import {
    listAllQueryKeyPrefix,
    listQueryKeyPrefix,
    projectsQueryKeyPrefix,
} from '@/helper/queryKeyPrefixes'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

export const projectsListQueryKeyPrefix = [projectsQueryKeyPrefix] as const

export const projectsQueryOptions = (
    clients: Partial<GrpcClientsContextValue> | null,
    filter: Partial<OmitDeepMessageFields<ProjectFiltersDto>> = {},
    loadAll = false
) =>
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryOptions({
        queryKey:
            loadAll ?
                [...projectsListQueryKeyPrefix, listAllQueryKeyPrefix, filter]
            :   [...projectsListQueryKeyPrefix, listQueryKeyPrefix, filter],
        queryFn: async ({ signal }) => {
            assertNotNullish(clients?.project)

            const internalFilter = create(ProjectFiltersDtoSchema, {
                deletedItemSelection:
                    filter.deletedItemSelection ?? DeletedItemSelection.BOTH,
                ids: filter.ids ?? [],
                sorting: filter.sorting ?? [],
                pagination: filter.pagination,
                searching: filter.searching ?? [],
                searchOperator: filter.searchOperator,
                userIds: filter.userIds ?? [],
                dateFrom: filter.dateFrom,
                dateTo: filter.dateTo,
            })

            if (!loadAll) {
                const response = await clients.project.getProjects(
                    internalFilter,
                    { signal }
                )

                return response
            }

            const projects = (
                await Array.fromAsync(
                    loadAllIter(
                        async f => {
                            const { projects: data, pagination } =
                                (await clients.project?.getProjects(f, {
                                    signal,
                                })) ?? {}

                            return { data, pagination }
                        },
                        internalFilter,
                        1000
                    )
                )
            ).flat()

            return {
                pagination: { totalItemCount: projects.length },
                projects,
            }
        },
    })

export const useProjects = (
    filter: Partial<OmitDeepMessageFields<ProjectFiltersDto>> = {},
    loadAll = false
) => {
    const clients = useGrpcClients()

    return useSuspenseQuery(projectsQueryOptions(clients, filter, loadAll))
}
